import { UserRightsTelephony } from "s2-rights";
import { createTemporaryId, isCreated } from "../utils/model";
import { safeObjectAssign } from "../utils/object";
import { Country } from "./Country";
import { LeadListPredictiveDialHaltReason } from "./LeadListPredictiveDialHaltReason";
import { LeadListSourceConfiguration } from "./LeadListSourceConfiguration";
import User from "./User";
import { ImportTaskStatus } from "./ImportTask";
import { LeadListResetTaskStatus } from "./LeadListResetTask";
import { text } from "../utils/i18n";
import { LeadListFilterConfig } from "./LeadListFilterConfig";
import { LeadListContactSummary } from "./LeadListContactSummary";

export class LeadListResetTaskReference {
  readonly taskId: string;
  readonly isExport: boolean;
  readonly status: LeadListResetTaskStatus;
}

export class ImportTaskReference {
  readonly needsConflictResolution?: boolean;
  readonly taskId: string;
  readonly status: ImportTaskStatus;
}

export class LeadListPredictiveDialRemovedUser {
  userId: string;
  cause: string | null;
}

export class LeadListPredictiveDialUser {
  userId: string;
  joinedAt: Date;

  constructor(deriveFrom?: Partial<LeadListPredictiveDialUser>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    if (this.joinedAt) {
      this.joinedAt = new Date(this.joinedAt);
    }
  }
}
export class LeadListActiveSourceConfiguration {
  readonly configurationId: string;
  readonly activatedAt: Date;
  readonly activeUntil: Date;

  constructor(deriveFrom?: Partial<LeadListActiveSourceConfiguration>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
    }

    this.activatedAt = new Date(this.activatedAt);
    this.activeUntil = new Date(this.activeUntil);
  }
}

/**
 * Represents a lead list, which is a set of contacts to be dialed.
 */
export default class LeadList {
  readonly id: string = createTemporaryId();
  readonly key = createTemporaryId();
  readonly serialId: number = null;
  readonly userId?: string;
  readonly date = new Date();
  readonly name: string = null;
  readonly predictiveDialAggression?: number = 50;
  readonly predictiveDialUsers: LeadListPredictiveDialUser[] = [];
  readonly predictiveDialRemovedUsers: LeadListPredictiveDialRemovedUser[] = [];
  readonly predictionsArchivedAt?: Date;
  readonly predictiveDialHaltReason?: LeadListPredictiveDialHaltReason;
  readonly accessUserId?: string;
  readonly accessTeamIds: string[] = [];
  readonly accessRoleIds: string[] = [];
  readonly excludeListIds: string[] = [];
  readonly unroutedContactFlow?: LeadListUnroutedContactFlowConfiguration;
  readonly skipCalledBackContacts: boolean = true;
  readonly repositionQuarantinedContacts: boolean = true;
  readonly defaultQuarantineDestination: boolean = false;
  readonly activeShuffleConfiguration?: LeadListActiveSourceConfiguration;
  readonly shuffleConfigurations?: LeadListSourceConfiguration[];
  readonly country?: Country;
  readonly isShuffle: boolean = false;
  readonly predictiveDialRingingTimeout?: number;
  readonly predictiveAutoBrakeThreshold?: number;
  readonly predictiveWaitForEvaluation: boolean = false;
  readonly importTask?: ImportTaskReference;
  readonly resetTask?: LeadListResetTaskReference;
  readonly voicemailHangUpThresholdId?: number;
  readonly voicemailHangUpUseDetector?: boolean;
  readonly filterConfig?: LeadListFilterConfig;

  /**
   * If currently synching, start timestamp.
   */
  readonly filterSynchingSince?: Date;
  readonly filterLastSyncAt?: Date;

  readonly isCallback: boolean = false;
  
  /**
   * Null means any project or none.
   * Unlike most kinds of entities, lead lists can be reusable in multiple projects.
   */
  readonly projectIds?: string[] = null;
  readonly contactSummaryCache?: LeadListContactSummary;
  readonly deletedAt?: Date = null;

  constructor(deriveFrom?: Partial<LeadList>) {
    if (deriveFrom) {
      Object.assign(this, deriveFrom);
      
      delete (this as any).ownerTeamIds;
    }

    if (this.predictiveDialUsers) {
      this.predictiveDialUsers = this.predictiveDialUsers.map(user => new LeadListPredictiveDialUser(user));
    }

    if (this.predictionsArchivedAt) {
      this.predictionsArchivedAt = new Date(this.predictionsArchivedAt);
    }

    if (this.activeShuffleConfiguration) {
      this.activeShuffleConfiguration = new LeadListActiveSourceConfiguration(this.activeShuffleConfiguration);
    }

    if (this.shuffleConfigurations) {
      this.shuffleConfigurations = this.shuffleConfigurations.map(config =>
        new LeadListSourceConfiguration(config));
    }

    if (this.isShuffle && !this.shuffleConfigurations?.length) {
      this.shuffleConfigurations = [new LeadListSourceConfiguration({ name: text`Standard` })];
    }

    if (this.filterConfig) {
      this.filterConfig = new LeadListFilterConfig(this.filterConfig);
    }

    if (this.filterSynchingSince) {
      this.filterSynchingSince = new Date(this.filterSynchingSince);
    }

    if (this.filterLastSyncAt) {
      this.filterLastSyncAt = new Date(this.filterLastSyncAt);
    }

    if (this.contactSummaryCache) {
      this.contactSummaryCache = new LeadListContactSummary(this.contactSummaryCache);
    }
  }

  get isCreated() {
    return isCreated(this.id);
  }

  /**
   * @returns Whether one or more users are using this lead list for predictive dialing.
   */
  get hasPredictiveDialUsers() {
    return this.predictiveDialUsers?.length > 0;
  }

  /**
   * Whether this lead list cannot be used for predictive dial.
   */
  get isPredictiveDialHalted() {
    return !!this.predictiveDialHaltReason;
  }

  /**
   * Whether this lead list cannot be used for predictive dial due to a system error.
   */
  get isPredictiveDialSystemHalted() {
    return this.predictiveDialHaltReason === LeadListPredictiveDialHaltReason.ContactRedialed
      || this.predictiveDialHaltReason === LeadListPredictiveDialHaltReason.UnresponsiveUsers;
  }

  getActiveShuffleConfiguration(): LeadListSourceConfiguration | null {
    if (!this.isShuffle) {
      return null;
    }

    const defaultConfig = this.shuffleConfigurations.find((config, index) => config.isDefault(index));

    if (!this.activeShuffleConfiguration || this.activeShuffleConfiguration.activeUntil < new Date()) {
      return defaultConfig;
    }

    return this.shuffleConfigurations.find(config =>
      config.id === this.activeShuffleConfiguration.configurationId) || defaultConfig;
  }

  hasActiveTemporaryShuffleConfiguration(): boolean {
    return Boolean(
      this.activeShuffleConfiguration && this.activeShuffleConfiguration.activeUntil > new Date()
    );
  }

  /**
   * Returns a list of source lead lists.
   * 
   * For shuffle lead lists, each source's ID is returned. For other lead lists,
   * their own ID is returned.
   */
  listSourceLeadListIds(): string[] {
    if (this.isShuffle && this.shuffleConfigurations?.length) {
      return this.shuffleConfigurations.flatMap(config =>
        config.sources.map(source => source.dialGroupId));
    }

    return [this.id];
  }

  /**
   * @returns Whether `user` has the right to update this lead list.
   */
  isUpdatableBy(user: User): boolean {
    if (user.isAdmin || this.accessUserId === user.id) {
      return true;
    }

    if (!this.accessTeamIds.length) {
      return false;
    }

    return user.hasRight(UserRightsTelephony.LeadListsUpdateForOwnTeam) &&
      Boolean(user.listTeamIds().find(teamId => this.accessTeamIds.includes(teamId)));
  }
}

export class LeadListUnroutedContactFlowConfiguration {
  readonly flowId: string;
  readonly enabled: boolean;
}

export class LeadListContact {
  readonly dialGroupId: string;
  readonly contactId: string;
  readonly reservedAt?: Date;

  /**
   * ID of user who reserved this contact.
   */
  readonly reserverUserId?: string;

  /**
   * Date to release this quarantined contact.
   */
  readonly quarantinedUntil?: Date;
  readonly skipReason?: string;
  readonly isUnreservable: boolean = false;

  constructor(deriveFrom?: Partial<LeadListContact>) {
    if (deriveFrom) {
      safeObjectAssign(this, deriveFrom);
    }

    if (this.reservedAt) {
      this.reservedAt = new Date(this.reservedAt);
    }

    if (this.quarantinedUntil) {
      this.quarantinedUntil = new Date(this.quarantinedUntil);
    }
  }

  /**
   * @returns Whether this contact is reserved by someone, including the current user.
   */
  get isReserved() {
    return this.reserverUserId != null;
  }

  get isQuarantined() {
    return this.quarantinedUntil != null && this.quarantinedUntil > new Date();
  }
}